/* eslint-disable consistent-return */
import moment from 'moment';
import _ from 'lodash';

const minutesInHour = 60;
const quartersInHour = 4;
const hoursAvailable = 16; //  6am - 10p
const quarterInMinutes = minutesInHour / quartersInHour; // 60 /4 = 15
const startCountingFrom6Not0 = 6 * quartersInHour; // 24 = 6 * quartersInHour

const assigned = 'assigned';
const assignedAlmostOverbooked = 'assigned-almost-overbooked';
const availability = 'availability';
const available = 'available';
const hourFormat = 'HH:mm:ss';
const overbooked = 'overbooked';
const zeroHours = '0:00';
const NEAR_CAPACITY_HOUR_OFFSET = 4;

export const timeMax = hoursAvailable * quartersInHour; // 64 = 6am - 10p * 4 quarter's

export const renderFirstLabel = (data, type) => {
  let countTime = 0;
  if (
    type === availability ||
    _.some(data, da => da.type === available || da.type === assignedAlmostOverbooked)
  ) {
    const filteredData =
      type === availability
        ? data
        : _.filter(data, da => da.type === available || da.type === assignedAlmostOverbooked);
    _.each(filteredData, (da, index) => {
      if (
        da.type === assignedAlmostOverbooked &&
        _.get(filteredData, [index, 'position_end']) ===
          _.get(filteredData, [index + 1, 'position_start'])
      ) {
        countTime += da.position_end - da.position_start - 1;
      } else {
        countTime += da.position_end - da.position_start;
      }
    });
  } else {
    return zeroHours;
  }

  const hours = _.floor(countTime / quartersInHour);
  const minutes = (countTime - hours * quartersInHour) * quarterInMinutes;

  if (hours && hours !== 0) {
    if (minutes && minutes !== 0) {
      return `${hours}:${minutes}`;
    }
    return `${hours}:00`;
  } else if (minutes && minutes !== 0) {
    return `0:${minutes}`;
  }

  return zeroHours;
};

export const renderAssignmentBarLabel = data => {
  const time = _.get(data, 'time');

  if (time) {
    const durationHours = time.hours();
    const durationMinutes = time.minutes();
    const minutes =
      durationMinutes === 0 ? '00' : _.last(_.split((durationMinutes / 100).toFixed(2), '.'));
    return `${durationHours}:${minutes}`;
  }
  return zeroHours;
};

export const refactorData = data => {
  const newData = [];

  if (!_.isEmpty(data)) {
    _.each(data, record => {
      const newRecord = {};
      const end = moment(record.end, hourFormat);
      const start = moment(record.start, hourFormat);

      newRecord.label_end = end.format('hA').slice(0, -1);
      newRecord.label_start = start.format('hA').slice(0, -1);

      newRecord.position_end =
        _.toInteger(end.format('H')) * quartersInHour +
        _.floor(_.toInteger(end.format('mm')) / quarterInMinutes) -
        startCountingFrom6Not0;
      newRecord.position_start =
        _.toInteger(start.format('H')) * quartersInHour +
        _.floor(_.toInteger(start.format('mm')) / quarterInMinutes) -
        startCountingFrom6Not0;

      newData.push(newRecord);
    });
  }
  return newData;
};

const getDurationAndAvailabilityTime = (personAvailability, personTaskAssignment) => {
  const personAvailabilityRefactored = refactorData(personAvailability);
  const assignmentDurationHours = personTaskAssignment.time.hours();
  const assignmentDurationMinutes = personTaskAssignment.time.minutes();
  const durationTime =
    assignmentDurationHours * quartersInHour + assignmentDurationMinutes / quarterInMinutes;
  const availabilityTime = _.sumBy(
    personAvailabilityRefactored,
    inter => inter.position_end - inter.position_start,
  );

  return {
    availabilityTime,
    durationTime,
  };
};

export const isOverbooked = (personAvailability, personTaskAssignment) => {
  const userHasNoAvailability = _.isEmpty(personAvailability);

  if (!userHasNoAvailability && !_.isEmpty(personTaskAssignment)) {
    const { availabilityTime, durationTime } = getDurationAndAvailabilityTime(
      personAvailability,
      personTaskAssignment,
    );

    return durationTime > availabilityTime;
  }

  return false;
};

export const isNotAvailable = (personAvailability, personTaskAssignment) =>
  _.isEmpty(personAvailability) && !_.isEmpty(personTaskAssignment);

export const isNearCapacity = (personAvailability, personTaskAssignment) => {
  const userHasNoAvailability = _.isEmpty(personAvailability);

  if (!userHasNoAvailability && !_.isEmpty(personTaskAssignment)) {
    const { availabilityTime, durationTime } = getDurationAndAvailabilityTime(
      personAvailability,
      personTaskAssignment,
    );
    const isNotOverbooked = durationTime <= availabilityTime;
    const availabilityWithOffset = availabilityTime - NEAR_CAPACITY_HOUR_OFFSET;
    const isBeforeAvailability =
      durationTime >= availabilityWithOffset && availabilityWithOffset > 0;

    return isNotOverbooked && isBeforeAvailability;
  }

  return false;
};

export const newCountAssignmentsData = (personAvailability, personTaskAssignment) => {
  if (!_.isEmpty(personAvailability) && _.isEmpty(personTaskAssignment)) {
    // without tasks
    const newData = _.clone(personAvailability);
    _.each(newData, interval => {
      interval.type = available;
    });
    return newData;
  } else if (_.isEmpty(personAvailability) && !_.isEmpty(personTaskAssignment)) {
    // tasks but with no available time
    const assignmentDurationHours = personTaskAssignment.time.hours();
    const assignmentDurationMinutes = personTaskAssignment.time.minutes();
    const taskDuration =
      assignmentDurationHours * quartersInHour + assignmentDurationMinutes / quarterInMinutes;
    const newData = {
      position_start: 0,
      position_end: taskDuration,
      type: overbooked,
    };
    newData.first_label = renderFirstLabel(newData);
    return [newData];
  } else if (!_.isEmpty(personAvailability) && !_.isEmpty(personTaskAssignment)) {
    const assignmentDurationHours = personTaskAssignment.time.hours();
    const assignmentDurationMinutes = personTaskAssignment.time.minutes();
    const taskDuration =
      assignmentDurationHours * quartersInHour + assignmentDurationMinutes / quarterInMinutes;
    const availabilityTime = _.sumBy(
      personAvailability,
      inter => inter.position_end - inter.position_start,
    );

    if (taskDuration > availabilityTime) {
      // more tasks than availability of the user
      const newData = [];
      _.each(personAvailability, (inter, index) => {
        if (index === _.size(personAvailability) - 1) {
          const newPositionEndDiff = taskDuration - availabilityTime;
          let timeMaxPosition = timeMax; // 6am - 10p * quartersInHour quoter's
          if (inter.position_end + newPositionEndDiff < timeMax) {
            timeMaxPosition = inter.position_end + newPositionEndDiff;
          }
          newData.push({
            ...inter,
            position_end: timeMaxPosition,
            type: overbooked,
          });
        } else {
          newData.push({ ...inter, type: overbooked });
        }
      });
      return newData;
    } else if (taskDuration === availabilityTime) {
      // tasks are equal availability
      const newData = [];
      _.each(personAvailability, (inter, index) => {
        if (index === 0) {
          newData.push({
            ...inter,
            type: assignedAlmostOverbooked,
          });
        } else {
          newData.push({ ...inter, type: assignedAlmostOverbooked });
        }
      });
      return newData;
    } else {
      const newDataWithPositions = [];
      const displayAssignedAlmostOverbooked =
        availabilityTime - taskDuration <= quartersInHour && availabilityTime - taskDuration > 0;
      let sigma = taskDuration;

      _.each(personAvailability, interval => {
        sigma = sigma < 0 ? 0 : sigma;

        const intervalDuration = interval.position_end - interval.position_start;
        const diff = sigma - intervalDuration;

        if (diff >= 0) {
          // user have time for task
          newDataWithPositions.push({ ...interval, type: assigned });
        } else {
          const assignedDuration = sigma;
          if (sigma !== 0) {
            // user have time only for part of task or time is almost exceed
            newDataWithPositions.push({
              ...interval,
              type: displayAssignedAlmostOverbooked ? assignedAlmostOverbooked : assigned,
              position_end: assignedDuration + interval.position_start,
            });
          }

          // user have time for task and after that should we have free time
          newDataWithPositions.push({
            ...interval,
            type: available,
            position_start: assignedDuration + interval.position_start,
            position_end: interval.position_end,
          });
        }

        sigma = diff;
      });

      return newDataWithPositions;
    }
  }
};
